import { useEffect, useRef, useState } from "react";
import { Save, Building2, ImagePlus, Trash2, Warehouse, QrCode, Landmark, Plus } from "lucide-react";
import { toast } from "sonner";
import { API, imgUrl } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyFirm = {
  name: "", logo_url: "", gstin: "", address: "", phone: "", email: "", qr_url: "",
  bank_name: "", bank_account: "", bank_ifsc: "", bank_branch: "", bank_holder: "", banks: [], tnc: "",
  pdf_bg_url: "", pdf_font_color: "", pdf_table_color: "", pdf_table_style: "classic", signature_url: "", dispatch_warehouse_id: "",
};
const emptyBank = { bank_name: "", bank_holder: "", bank_account: "", bank_ifsc: "", bank_branch: "", qr_url: "" };

function FirmCard({ firm, tag, warehouses, simple, testid, onSaved }) {
  const [form, setForm] = useState({ ...emptyFirm, ...firm });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const logoRef = useRef(null);
  const qrRefs = useRef({});

  useEffect(() => {
    const f = { ...emptyFirm, ...firm };
    if (!simple && !(f.banks || []).length && f.bank_name) {
      f.banks = [{ bank_name: f.bank_name, bank_holder: f.bank_holder, bank_account: f.bank_account, bank_ifsc: f.bank_ifsc, bank_branch: f.bank_branch, qr_url: f.qr_url || "" }];
    }
    setForm(f);
  }, [firm?.id]);

  const upload = async (file, apply) => {
    setUploading("x");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "Branding");
      const { data } = await API.post("/upload", fd);
      apply(data.url);
      toast.success("Uploaded — click Save to apply");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const setBank = (bi, k, v) => setForm((f) => ({ ...f, banks: f.banks.map((b, idx) => (idx === bi ? { ...b, [k]: v } : b)) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Firm name is required");
    setSaving(true);
    try {
      const payload = { ...form };
      const first = (form.banks || [])[0];
      if (first) {
        Object.assign(payload, { bank_name: first.bank_name, bank_holder: first.bank_holder, bank_account: first.bank_account, bank_ifsc: first.bank_ifsc, bank_branch: first.bank_branch, qr_url: first.qr_url || "" });
      } else {
        Object.assign(payload, { bank_name: "", bank_holder: "", bank_account: "", bank_ifsc: "", bank_branch: "", qr_url: "" });
      }
      await API.put(`/firms/${firm.id}`, payload);
      toast.success(`${form.name} saved — new PDFs will use these details`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="h-4 w-4 text-indigo-500" /> {form.name || "Firm"} <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-600">{tag}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={simple ? "" : "grid grid-cols-1 gap-4"}>
          <div>
            <Label>Logo (PDF header)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {form.logo_url ? (
                <img src={imgUrl(form.logo_url)} alt="logo" className="h-14 w-14 rounded-md border border-slate-200 object-contain" data-testid={`${testid}-logo-preview`} />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-md bg-slate-100 text-slate-400"><Building2 className="h-6 w-6" /></div>
              )}
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], (url) => setForm((f) => ({ ...f, logo_url: url })))} data-testid={`${testid}-logo-input`} />
              <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={!!uploading} data-testid={`${testid}-logo-upload`}>
                <ImagePlus className="mr-1 h-4 w-4" /> {uploading ? "Uploading..." : form.logo_url ? "Change" : "Upload"}
              </Button>
              {form.logo_url && (
                <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, logo_url: "" })} data-testid={`${testid}-logo-remove`} aria-label="Remove logo"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              )}
            </div>
          </div>
        </div>
        {!simple && (
          <div>
            <Label>Signature / Stamp (prints above "Authorised Signatory" on PDFs)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {form.signature_url ? (
                <img src={imgUrl(form.signature_url)} alt="signature" className="h-14 max-w-[140px] rounded-md border border-slate-200 object-contain" data-testid={`${testid}-sign-preview`} />
              ) : (
                <div className="flex h-14 w-24 items-center justify-center rounded-md bg-slate-100 text-[10px] text-slate-400">No signature</div>
              )}
              <input ref={(el) => (qrRefs.current.sign = el)} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], (url) => setForm((f) => ({ ...f, signature_url: url })))} data-testid={`${testid}-sign-input`} />
              <Button variant="outline" size="sm" onClick={() => qrRefs.current.sign?.click()} disabled={!!uploading} data-testid={`${testid}-sign-upload`}>
                <ImagePlus className="mr-1 h-4 w-4" /> {form.signature_url ? "Change" : "Upload"}
              </Button>
              {form.signature_url && (
                <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, signature_url: "" })} data-testid={`${testid}-sign-remove`} aria-label="Remove signature"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              )}
            </div>
          </div>
        )}
        <div className={simple ? "" : "grid grid-cols-2 gap-4"}>
          <div>
            <Label>Firm Name</Label>
            <Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid={`${testid}-name-input`} />
          </div>
          {!simple && (
            <div>
              <Label>GSTIN</Label>
              <Input value={form.gstin} onChange={set("gstin")} className="mt-1.5" data-testid={`${testid}-gstin-input`} />
            </div>
          )}
        </div>
        {!simple && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid={`${testid}-phone-input`} />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={set("email")} className="mt-1.5" data-testid={`${testid}-email-input`} />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea rows={2} value={form.address} onChange={set("address")} className="mt-1.5" data-testid={`${testid}-address-input`} />
            </div>
            <div>
              <Label>Terms &amp; Conditions (printed on every Performa &amp; Sale Invoice PDF)</Label>
              <Textarea rows={4} value={form.tnc} onChange={set("tnc")} placeholder={"1. Goods once sold will not be taken back or exchanged.\n2. Payment will be 100% advance."} className="mt-1.5" data-testid={`${testid}-tnc-input`} />
            </div>
            <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3" data-testid={`${testid}-pdf-format`}>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">PDF Format (applies to Performa &amp; Sale Invoice)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Background / Watermark image (full page, low opacity)</Label>
                  <div className="mt-1 flex items-center gap-2">
                    {form.pdf_bg_url ? (
                      <img src={imgUrl(form.pdf_bg_url)} alt="PDF background" className="h-12 w-10 rounded border border-slate-200 object-cover" data-testid={`${testid}-bg-preview`} />
                    ) : (
                      <div className="flex h-12 w-10 items-center justify-center rounded bg-slate-100 text-slate-400"><ImagePlus className="h-4 w-4" /></div>
                    )}
                    <input ref={(el) => (qrRefs.current.bg = el)} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], (url) => setForm((f) => ({ ...f, pdf_bg_url: url })))} data-testid={`${testid}-bg-input`} />
                    <Button variant="outline" size="sm" className="h-7" onClick={() => qrRefs.current.bg?.click()} disabled={!!uploading} data-testid={`${testid}-bg-upload`}>{form.pdf_bg_url ? "Change" : "Upload"}</Button>
                    {form.pdf_bg_url && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setForm({ ...form, pdf_bg_url: "" })} data-testid={`${testid}-bg-remove`} aria-label="Remove background"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>}
                  </div>
                  <p className="mt-1 text-[10px] text-slate-400">A4 portrait image works best. Drawn faded behind the whole page — fully independent from the logo above; uploading one never affects the other.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Font colour</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input type="color" value={form.pdf_font_color || "#111111"} onChange={(e) => setForm({ ...form, pdf_font_color: e.target.value })} className="h-8 w-10 cursor-pointer rounded border border-slate-200" data-testid={`${testid}-font-color`} />
                      <span className="text-xs text-slate-500">{form.pdf_font_color || "#111111"}</span>
                    </div>
                  </div>
                  <div>
                    <Label>Table colour</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input type="color" value={form.pdf_table_color || "#334155"} onChange={(e) => setForm({ ...form, pdf_table_color: e.target.value })} className="h-8 w-10 cursor-pointer rounded border border-slate-200" data-testid={`${testid}-table-color`} />
                      <span className="text-xs text-slate-500">{form.pdf_table_color || "#334155"}</span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label>Table style</Label>
                    <Select value={form.pdf_table_style || "classic"} onValueChange={(v) => setForm({ ...form, pdf_table_style: v })}>
                      <SelectTrigger className="mt-1" data-testid={`${testid}-table-style`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="classic" data-testid={`${testid}-table-style-classic`}>Classic Grid — full light borders (like your reference)</SelectItem>
                        <SelectItem value="zebra" data-testid={`${testid}-table-style-zebra`}>Modern Zebra — alternating row shading, no vertical lines</SelectItem>
                        <SelectItem value="modern" data-testid={`${testid}-table-style-modern`}>Modern Header — coloured header bar, clean rows</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3" data-testid={`${testid}-banks`}>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Banks (Payment Method block on PDF — pick one while making a document)</div>
                <Button variant="outline" size="sm" className="h-7" onClick={() => setForm((f) => ({ ...f, banks: [...(f.banks || []), { ...emptyBank }] }))} data-testid={`${testid}-bank-add`}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add Bank
                </Button>
              </div>
              {(form.banks || []).length === 0 && <p className="py-2 text-xs text-slate-400">No banks yet — add at least one so it can be shown on invoices.</p>}
              {(form.banks || []).map((b, bi) => (
                <div key={bi} className="mb-3 rounded-md border border-slate-200 bg-white p-3" data-testid={`${testid}-bank-${bi}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Landmark className="h-3.5 w-3.5 text-indigo-500" /> Bank {bi + 1}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setForm((f) => ({ ...f, banks: f.banks.filter((_, idx) => idx !== bi) }))} data-testid={`${testid}-bank-${bi}-remove`} aria-label="Remove bank"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Bank Name</Label><Input value={b.bank_name} onChange={(e) => setBank(bi, "bank_name", e.target.value)} placeholder="HDFC Bank" className="mt-1" data-testid={`${testid}-bank-${bi}-name`} /></div>
                    <div><Label>Account Holder</Label><Input value={b.bank_holder} onChange={(e) => setBank(bi, "bank_holder", e.target.value)} className="mt-1" data-testid={`${testid}-bank-${bi}-holder`} /></div>
                    <div><Label>Account No.</Label><Input value={b.bank_account} onChange={(e) => setBank(bi, "bank_account", e.target.value)} className="mt-1" data-testid={`${testid}-bank-${bi}-account`} /></div>
                    <div><Label>IFSC</Label><Input value={b.bank_ifsc} onChange={(e) => setBank(bi, "bank_ifsc", e.target.value)} className="mt-1" data-testid={`${testid}-bank-${bi}-ifsc`} /></div>
                    <div><Label>Branch</Label><Input value={b.bank_branch} onChange={(e) => setBank(bi, "bank_branch", e.target.value)} className="mt-1" data-testid={`${testid}-bank-${bi}-branch`} /></div>
                    <div>
                      <Label>Payment QR</Label>
                      <div className="mt-1 flex items-center gap-2">
                        {b.qr_url ? (
                          <img src={imgUrl(b.qr_url)} alt="QR" className="h-10 w-10 rounded border border-slate-200 object-contain" data-testid={`${testid}-bank-${bi}-qr-preview`} />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded bg-slate-100 text-slate-400"><QrCode className="h-4 w-4" /></div>
                        )}
                        <input ref={(el) => (qrRefs.current[bi] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], (url) => setBank(bi, "qr_url", url))} data-testid={`${testid}-bank-${bi}-qr-input`} />
                        <Button variant="outline" size="sm" className="h-7" onClick={() => qrRefs.current[bi]?.click()} disabled={!!uploading} data-testid={`${testid}-bank-${bi}-qr-upload`}>{b.qr_url ? "Change" : "Upload"}</Button>
                        {b.qr_url && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setBank(bi, "qr_url", "")} data-testid={`${testid}-bank-${bi}-qr-remove`} aria-label="Remove QR"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {simple && (
          <div>
            <Label>Default Dispatch Warehouse (challan source)</Label>
            <Select value={form.dispatch_warehouse_id || "none"} onValueChange={(v) => setForm({ ...form, dispatch_warehouse_id: v === "none" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid={`${testid}-dispatch-select`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ask at dispatch</SelectItem>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving} data-testid={`${testid}-save`}>
          <Save className="mr-2 h-4 w-4" /> {saving ? "Saving..." : `Save ${tag}`}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [firms, setFirms] = useState([]);
  const [config, setConfig] = useState({ b2b_warehouse_id: "", firm1_id: "", firm2_id: "" });
  const [warehouses, setWarehouses] = useState([]);
  const [b2bId, setB2bId] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);

  const load = () => {
    API.get("/firms").then((r) => setFirms(r.data)).catch(() => {});
    API.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    API.get("/settings/config").then((r) => { setConfig(r.data); setB2bId(r.data.b2b_warehouse_id || ""); }).catch(() => {});
  };
  useEffect(load, []);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await API.put("/settings/config", { b2b_warehouse_id: b2bId });
      toast.success("B2B receiving warehouse saved — challan receipts will land there");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingCfg(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10 text-sm text-slate-500" data-testid="settings-no-access">
        Only the admin can manage company settings.
      </div>
    );
  }

  const firm1 = firms.find((f) => f.id === config.firm1_id) || firms[0];
  const firm2 = firms.find((f) => f.id === config.firm2_id) || firms[1];

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Settings — Firms</h1>
        <p className="text-sm text-slate-500">Company 1 handles stock only. Company 2's profile, banks & QR are used on Performa / Sale Invoice PDFs</p>
      </div>

      {firm1 && <FirmCard firm={firm1} tag="Company 1 · D2C" warehouses={warehouses.filter((w) => w.id !== b2bId)} simple testid="firm1-card" onSaved={load} />}

      {firm2 && (
        <>
          <FirmCard firm={firm2} tag="Company 2 · B2B" warehouses={warehouses} testid="firm2-card" onSaved={load} />
          <Card className="border-slate-200" data-testid="firm2-warehouse-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><Warehouse className="h-4 w-4 text-indigo-500" /> Company 2 — B2B Receiving Warehouse</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">
                Challan receipts from Company 1 land in this warehouse, and its stock &amp; accounting stay hidden from Company 1 staff (Stock Team / Stock Head).
              </p>
              <Select value={b2bId || "none"} onValueChange={(v) => setB2bId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="b2b-warehouse-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not configured</SelectItem>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={saveConfig} disabled={savingCfg} data-testid="b2b-config-save">
                <Save className="mr-2 h-4 w-4" /> {savingCfg ? "Saving..." : "Save B2B Warehouse"}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
